import { AnimatePresence, motion } from 'motion/react';
import { useEffect } from 'react';
import { zeroPad } from 'react-countdown';
import { useTranslation } from 'react-i18next';
import { twJoin } from 'tailwind-merge';

import IconButton from '@/components/ui/custom/buttons/IconButton';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { ModularCard } from '@/components/ui/custom/modular/ModularCard';
import useToggle from '@/data/hook/useToggle';
import { TempLesson } from '@/models/Lesson';
import { TempLessonBlock } from '@/models/_deprecated/LessonBlock';
import { bodyTextSmall } from '@/styles/styles';
import { errorMessage } from '@/utils/animations/commom';
import { scrollBarYClassName } from '@/utils/scrollBarClassName';
import { PencilIcon } from '@heroicons/react/24/outline';
import { getPreviousBlocksLessons } from './CourseStudioLessons.hooks';
import { shouldDisplayDirectLessons } from '@/utils/courseStudio/matrix';

export type onClickArgs = {
  block?: TempLessonBlock;
  lesson: TempLesson;
};

type Props = {
  blocks?: TempLessonBlock[];
  directLessons?: TempLesson[];
  hasOpen?: boolean;
  onClick?: ({ block, lesson }: onClickArgs) => void;
  onEdit?: () => void;
  selectedLesson?: TempLesson;
  disabled?: boolean;
  className?: string;
};

export default function CourseStudioMatrix(props: Props) {
  const {
    blocks = [],
    directLessons = [],
    hasOpen,
    onClick,
    selectedLesson,
    onEdit,
    disabled,
    className,
  } = props;

  const { isOpen, toggle, open } = useToggle(hasOpen);

  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });

  useEffect(() => {
    if (hasOpen) open();
  }, [open, hasOpen]);

  const filteredBlocks = blocks.filter(
    block => block.lessons.length > 0 && block.name,
  );

  const filteredDirectLessons = directLessons.filter(lesson => lesson.name);

  const hasContent =
    filteredBlocks.length > 0 || filteredDirectLessons.length > 0;

  return (
    <AnimatePresence>
      {hasContent && (
        <motion.div
          className={twJoin(isOpen && className)}
          style={
            isOpen
              ? {
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }
              : undefined
          }
          {...errorMessage}
        >
          <ModularCard
            className={{ base: twJoin('flex flex-col', isOpen && 'h-full') }}
          >
            <ModularCard.Header
              title={t('matrix')}
              isOpen={isOpen}
              onOpen={toggle}
              className={{ title: 'text-primary', base: 'shrink-0' }}
            >
              <ConditionalRenderer condition={onEdit}>
                <IconButton
                  icon={<PencilIcon className="w-4 h-4 text-primary" />}
                  onClick={onEdit}
                  tooltip={t('edit')}
                />
              </ConditionalRenderer>
            </ModularCard.Header>

            <ModularCard.Accordion
              isOpen={isOpen}
              className={{ base: 'flex flex-col overflow-hidden' }}
            >
              <ul
                className={twJoin(
                  'flex flex-col gap-2 p-4 pt-0',
                  isOpen && 'overflow-y-auto max-h-[calc(100vh-22rem)]',
                  scrollBarYClassName,
                )}
              >
                <ConditionalRenderer
                  condition={shouldDisplayDirectLessons(
                    filteredDirectLessons,
                    filteredBlocks,
                  )}
                >
                  {filteredDirectLessons.length > 0 && (
                    <DirectLessonsMatrixItem
                      lessons={filteredDirectLessons}
                      disabled={disabled}
                      onClick={onClick}
                      selectedLesson={selectedLesson}
                    />
                  )}
                </ConditionalRenderer>
                {filteredBlocks.map((block, index) => {
                  const realBlockIndex = blocks.findIndex(
                    b => (b.id || b.tempId) === (block.id || block.tempId),
                  );
                  const previousLessonsCount = filteredDirectLessons.length;
                  return (
                    <CourseStudioMatrixItem
                      key={block.id || block.tempId}
                      lessons={block.lessons}
                      block={block}
                      index={index}
                      blockIndex={realBlockIndex}
                      blocks={blocks}
                      disabled={disabled}
                      onClick={onClick}
                      selectedLesson={selectedLesson}
                      previousLessonsCount={previousLessonsCount}
                    />
                  );
                })}
              </ul>
            </ModularCard.Accordion>
          </ModularCard>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

type CourseStudioMatrixItemProps = {
  index: number;
  blockIndex: number;
  blocks: TempLessonBlock[];
  block: TempLessonBlock;
  lessons?: TempLesson[];
  onClick?: ({ block, lesson }: onClickArgs) => void;
  selectedLesson?: TempLesson;
  disabled?: boolean;
  previousLessonsCount?: number;
};

function CourseStudioMatrixItem(props: CourseStudioMatrixItemProps) {
  const {
    index,
    blockIndex,
    blocks,
    lessons = [],
    block,
    onClick,
    selectedLesson,
    disabled,
    previousLessonsCount = 0,
  } = props;

  const { t } = useTranslation('translation', {
    keyPrefix: 'courseStudio',
  });

  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const filteredLessons = lessons.filter(lesson => lesson.name);

  const previousBlocksLessons =
    getPreviousBlocksLessons(blockIndex, blocks) + previousLessonsCount;

  const formatLessonName = (lesson: TempLesson, lessonIndex: number) => {
    return `${tCommon('lesson', {
      order: zeroPad(previousBlocksLessons + lessonIndex + 1, 2),
    })} - ${lesson.name}`;
  };

  return (
    <li className="flex flex-col gap-2">
      <ConditionalRenderer condition={!!block.name}>
        <p className={twJoin(bodyTextSmall, 'text-neutral font-500')}>
          {t('block', { index: zeroPad(index + 1, 2) })} - {block.name}
        </p>
      </ConditionalRenderer>
      <ul className="flex flex-col gap-2 pl-3">
        {filteredLessons.map((lesson, lessonIndex) => (
          <li
            key={lesson.id || lesson.tempId}
            className={twJoin(
              bodyTextSmall,
              'text-neutral',
              'flex flex-1 gap-2 items-center',
              onClick && 'cursor-pointer',
              disabled && 'disabled',
            )}
            onClick={() => onClick?.({ block, lesson })}
          >
            <span className="text-base-content font-500">•</span>
            <span
              className={twJoin(
                'break-words max-w-full',
                onClick &&
                  'hover:underline hover:underline-offset-2 hover:text-primary transition-all duration-100',
                selectedLesson?.id === lesson.id &&
                  selectedLesson &&
                  'font-500 text-primary',
              )}
            >
              {formatLessonName(lesson, lessonIndex)}
            </span>
          </li>
        ))}
      </ul>
    </li>
  );
}

type DirectLessonsMatrixItemProps = {
  lessons: TempLesson[];
  onClick?: ({ block, lesson }: onClickArgs) => void;
  selectedLesson?: TempLesson;
  disabled?: boolean;
};

function DirectLessonsMatrixItem(props: DirectLessonsMatrixItemProps) {
  const { lessons = [], onClick, selectedLesson, disabled } = props;

  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const formatLessonName = (lesson: TempLesson, lessonIndex: number) => {
    return `${tCommon('lesson', {
      order: zeroPad(lessonIndex + 1, 2),
    })} - ${lesson.name}`;
  };

  if (lessons.length === 0) return null;

  return (
    <li className="flex flex-col gap-2">
      <ul className="flex flex-col gap-2">
        {lessons.map((lesson, lessonIndex) => (
          <li
            key={lesson.id || lesson.tempId}
            className={twJoin(
              bodyTextSmall,
              'text-neutral',
              'flex flex-1 gap-2 items-center',
              onClick && 'cursor-pointer',
              disabled && 'disabled',
            )}
            onClick={() => onClick?.({ lesson })}
          >
            <span className="text-base-content font-500">•</span>
            <span
              className={twJoin(
                'break-words max-w-full',
                onClick &&
                  'hover:underline hover:underline-offset-2 hover:text-primary transition-all duration-100',
                selectedLesson?.id === lesson.id &&
                  selectedLesson &&
                  'font-500 text-primary',
              )}
            >
              {formatLessonName(lesson, lessonIndex)}
            </span>
          </li>
        ))}
      </ul>
    </li>
  );
}
